import { apiSlice } from '../../api';
import type {
  DeleteMerchantDto,
  DeleteMerchantResult,
  GetMerchantByIdDTO,
  GetMerchantByIdResult,
  GetMerchantConfigDTO,
  GetMerchantConfigResult,
  GetMerchantDepositRequestDetailsDTO,
  GetMerchantDepositRequestDetailsResult,
  GetMerchantDepositRequestsDTO,
  GetMerchantDepositRequestsResult,
  GetMerchantDTO,
  GetMerchantResult,
  PatchMerchantConfigDTO,
  PatchMerchantConfigResult,
  PostMerchantConfigDTO,
  PostMerchantConfigResult,
  PostMerchantDto,
  PostMerchantResult,
  PutMerchantDTO,
  PutMerchantResult,
  GetMerchantWalletsDTO,
  GetMerchantWalletsResult,
  GetCoinsDTO,
  GetCoinsResult,
} from './types';

export const merchantEndpoints = apiSlice.injectEndpoints({
  endpoints: (build) => {
    return {
      getMerchantCoinConfig: build.query<GetMerchantResult, GetMerchantDTO>({
        query: () => ({
          url: '/admin/v1/merchant-coin-configs',
          method: 'GET',
        }),
      }),

      getMerchantCoinConfigById: build.query<GetMerchantByIdResult, GetMerchantByIdDTO>({
        query: ({ profileUid }) => ({
          url: `/admin/v1/profiles/${profileUid}/merchant-coin-configs`,
          method: 'GET',
        }),
      }),

      getMerchantDepositRequests: build.query<
        GetMerchantDepositRequestsResult,
        GetMerchantDepositRequestsDTO
      >({
        query: ({ merchant_profile_uid, params }) => ({
          url: `/admin/v1/merchants/${merchant_profile_uid}/deposit-requests`,
          method: 'GET',
          params,
        }),
      }),

      getMerchantDepositRequestDetails: build.query<
        GetMerchantDepositRequestDetailsResult,
        GetMerchantDepositRequestDetailsDTO
      >({
        query: ({ merchant_profile_uid, request_uid }) => ({
          url: `/admin/v1/merchants/${merchant_profile_uid}/deposit-requests/${request_uid}`,
          method: 'GET',
        }),
      }),

      getCoinsByMerchantUid: build.query<GetCoinsResult, GetCoinsDTO>({
        query: ({ merchant_profile_uid, deposit_request_uid }) => ({
          url: `/pay/v1/merchants/${merchant_profile_uid}/deposit-requests/${deposit_request_uid}/available-coins`,
          method: 'GET',
        }),
      }),

      putMerchantCoinConfigGoups: build.mutation<PutMerchantResult, PutMerchantDTO>({
        query: ({ body, merchant_coin_config_id }) => ({
          url: `/admin/v1/merchant-coin-configs/${merchant_coin_config_id}`,
          method: 'PUT',
          body,
        }),
      }),

      postMerchantCoinConfigGoups: build.mutation<PostMerchantResult, PostMerchantDto>({
        query: ({ body, profile_uid }) => ({
          url: `/admin/v1/profiles/${profile_uid}/merchant-coin-configs`,
          method: 'POST',
          body,
        }),
      }),

      deleteMerchantCoinConfigGoups: build.mutation<DeleteMerchantResult, DeleteMerchantDto>({
        query: ({ merchant_coin_config_id, profile_uid }) => ({
          url: `/admin/v1/profiles/${profile_uid}/merchant-coin-configs/${merchant_coin_config_id}`,
          method: 'DELETE',
        }),
      }),

      getMerchantWallets: build.query<GetMerchantWalletsResult, GetMerchantWalletsDTO>({
        query: ({ profileUid }) => ({
          url: `/v1/profiles/${profileUid}/wallets/merchant`,
          method: 'GET',
        }),
      }),

      getMerchantConfig: build.query<GetMerchantConfigResult, GetMerchantConfigDTO>({
        query: ({ profileUid }) => ({
          url: `/admin/v1/profiles/${profileUid}/merchant-config`,
          method: 'GET',
        }),
      }),

      postMerchantConfig: build.mutation<PostMerchantConfigResult, PostMerchantConfigDTO>({
        query: ({ profileUid }) => ({
          url: `/admin/v1/profiles/${profileUid}/merchant-config`,
          method: 'POST',
        }),
      }),

      patchMerchantConfig: build.mutation<PatchMerchantConfigResult, PatchMerchantConfigDTO>({
        query: ({ profileUid, body }) => ({
          url: `/admin/v1/profiles/${profileUid}/merchant-config`,
          method: 'PATCH',
          body,
        }),
      }),
    };
  },
});

export const {
  useGetMerchantCoinConfigQuery,
  useLazyGetMerchantCoinConfigQuery,
  useGetMerchantCoinConfigByIdQuery,
  useLazyGetMerchantCoinConfigByIdQuery,
  usePutMerchantCoinConfigGoupsMutation,
  usePostMerchantCoinConfigGoupsMutation,
  useDeleteMerchantCoinConfigGoupsMutation,
  useGetMerchantDepositRequestsQuery,
  useLazyGetMerchantDepositRequestsQuery,
  useGetMerchantDepositRequestDetailsQuery,
  useLazyGetMerchantDepositRequestDetailsQuery,
  useGetMerchantConfigQuery,
  useLazyGetMerchantConfigQuery,
  usePatchMerchantConfigMutation,
  usePostMerchantConfigMutation,
  useGetMerchantWalletsQuery,
  useLazyGetMerchantWalletsQuery,
  useGetCoinsByMerchantUidQuery,
  useLazyGetCoinsByMerchantUidQuery,
} = merchantEndpoints;

export type RateMarkupsController = typeof merchantEndpoints;
